import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Select,
  SelectItem,
} from '@nextui-org/react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { COLORS } from 'common/src/utils/colors';
import {
  getSettlementsFobVsExporters,
  getUniqueVarietiesWithCount,
} from 'common/src/api/settlements';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import ModalSlideFilter from '../../../../../common/src/components/filters/modal-sidebar-filter';
import { Button } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';
import { logUserAction } from '../../../services/actionsService';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import SeasonFilter from '../../../../../common/src/components/filters/SeasonFilter';
import MethologyFobExportersModal from '../../../components/modals/MethologyFobExportersModal';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import Header from '../../../components/layouts/Header';
import {
  Alert,
  EmptyState,
  FiltersToggle,
  LoadingState,
  PageLayout,
} from '../../../components/ui';

interface BoxPlotData {
  own:
  | {
    label?: string;
    receiver?: string;
    value: number;
    kilos: number;
  }[]
  | null;
  others: {
    min: number;
    q1: number;
    median: number;
    q3: number;
    max: number;
  };
  othersKg?: number;
}

interface WeekData {
  [caliber: string]: BoxPlotData;
}

interface VarietyData {
  [week: string]: WeekData;
}

interface ComparativeData {
  [variety: string]: VarietyData;
}

// Add caliber selector
const CALIBERS = ['L', 'XL', 'J', '2J', '3J', '4J', '5J'];

const SettlementsComparativeExportersEvolution = () => {
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const [data, setData] = useState<ComparativeData>({});
  const [loading, setLoading] = useState(true);
  const [varietiesLoading, setVarietiesLoading] = useState(true);
  const [selectedVariety, setSelectedVariety] = useState<string>('');
  const [selectedCaliber, setSelectedCaliber] = useState<string>('2J');
  const [visualization, setVisualization] = useState<'chart' | 'table'>(
    'chart'
  );
  const [varieties, setVarieties] = useState<
    Array<{ variety: string; count: number }>
  >([]);
  const [weeks, setWeeks] = useState<string[]>([]);
  const { userProfile, session, darkMode } = useUserStore();
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [priceView, setPriceView] = useState<'kg' | 'box'>('kg');
  const [methodology, setMethodology] = useState<'by_volume' | 'by_exporter'>(
    'by_exporter'
  );
  const [dateMethodology, setDateMethodology] = useState<
    'date_sold' | 'eta' | 'etd'
  >('eta');
  const [isMethodologyModalOpen, setIsMethodologyModalOpen] = useState(false);
  const [sinisterFilter, setSinisterFilter] = useState<
    'all' | 'sinister' | 'non_sinister'
  >('all');

  const [separation, setSeparation] = useState<
    'none' | 'by_label' | 'by_receiver'
  >('none');

  const calculatePercentile = (
    value: number | null,
    boxPlotData: {
      min: number;
      q1: number;
      median: number;
      q3: number;
      max: number;
    }
  ) => {
    if (!value) return null;
    if (value >= boxPlotData.max) return 100;
    if (value <= boxPlotData.min) return 0;

    // Calculate which quartile the value falls into
    if (value <= boxPlotData.q1) {
      // Between min and Q1 (0-25%)
      return Math.round(
        ((value - boxPlotData.min) / (boxPlotData.q1 - boxPlotData.min)) * 25
      );
    } else if (value <= boxPlotData.median) {
      // Between Q1 and median (25-50%)
      return Math.round(
        25 +
        ((value - boxPlotData.q1) / (boxPlotData.median - boxPlotData.q1)) *
        25
      );
    } else if (value <= boxPlotData.q3) {
      // Between median and Q3 (50-75%)
      return Math.round(
        50 +
        ((value - boxPlotData.median) /
          (boxPlotData.q3 - boxPlotData.median)) *
        25
      );
    } else {
      // Between Q3 and max (75-100%)
      return Math.round(
        75 +
        ((value - boxPlotData.q3) / (boxPlotData.max - boxPlotData.q3)) * 25
      );
    }
  };

  // Fetch varieties first
  useEffect(() => {
    const fetchVarieties = async () => {
      try {
        setVarietiesLoading(true);
        // Clear selected variety to prevent premature data fetch
        setSelectedVariety('');

        // Skip if not cherry
        if (selectedSpecies?.id !== 2) {
          setVarietiesLoading(false);
          return;
        }

        const varietiesData = await getUniqueVarietiesWithCount(
          session,
          selectedSeason,
          selectedSpecies?.id
        );

        if (userProfile?.exporter?.hasDemo) {
          // For demo users, set specific defaults
          setVarieties([{ variety: 'LAPINS', count: 100 }]);

          setSelectedVariety('LAPINS');
        } else if (varietiesData.length > 0) {
          setVarieties(varietiesData);

          setSelectedVariety(varietiesData[0].variety);
        }
      } catch (error) {
        console.error('Error fetching varieties:', error);
      } finally {
        setVarietiesLoading(false);
      }
    };

    if (session) {
      fetchVarieties();
    }
  }, [selectedSeason, selectedSpecies?.id]);

  // Fetch data for selected variety
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedVariety) return;

      // Skip if not cherry
      if (selectedSpecies?.id !== 2) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getSettlementsFobVsExporters(
          session,
          methodology,
          selectedSeason,
          selectedSpecies?.id,
          sinisterFilter,
          separation,
          null,
          selectedVariety,
          dateMethodology
        );

        if (!data) {
          throw new Error('No response received from server');
        }

        setData(data);

        // Get unique weeks
        const allWeeks = new Set<string>();

        Object.entries(data[selectedVariety] || {}).forEach(([week]) => {
          allWeeks.add(week);
        });

        const sortedWeeks = Array.from(allWeeks).sort((a, b) => {
          const weekA = parseInt(a.split('-W')[1]);
          const weekB = parseInt(b.split('-W')[1]);

          // If one week is >= 27 and the other is < 27, the >= 27 comes first
          if (weekA >= 27 && weekB < 27) return -1;
          if (weekA < 27 && weekB >= 27) return 1;

          // Otherwise, sort normally
          return weekA - weekB;
        });

        setWeeks(sortedWeeks);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (
      session &&
      selectedVariety &&
      !varietiesLoading &&
      selectedSpecies?.id
    ) {
      fetchData();
    }
  }, [
    methodology,
    sinisterFilter,
    separation,
    selectedVariety,
    varietiesLoading,
    dateMethodology,
    selectedSpecies?.id,
  ]);

  useEffect(() => {
    logUserAction(
      'view',
      'page',
      'settlements-comparative-exporters-evolution'
    );
  }, []);

  const chartOptions: ApexOptions = {
    chart: {
      type: 'boxPlot',
      height: 600,
      foreColor: darkMode ? '#A1A1AA' : '#71717A',
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      animations: {
        enabled: true,
      },
    },
    colors: [
      COLORS.cherry,
      COLORS.lightGreen,
      COLORS.blue,
      COLORS.gray,
      COLORS.yellow,
    ],
    plotOptions: {
      boxPlot: {
        colors: {
          upper: COLORS.cherry,
          lower: COLORS.cherry,
        },
      },
    },
    xaxis: {
      type: 'category',
      title: {
        text: 'Semana',
      },
      crosshairs: {
        show: true,
        width: 1,
        opacity: 0.9,
        stroke: {
          color: '#b6b6b6',
          width: 1,
          dashArray: 0,
        },
        fill: {
          type: 'solid',
          color: '#B1B9C4',
          gradient: {
            colorFrom: '#D8E3F0',
            colorTo: '#BED1E6',
            stops: [0, 100],
            opacityFrom: 0.4,
            opacityTo: 0.5,
          },
        },
        dropShadow: {
          enabled: false,
          top: 0,
          left: 0,
          blur: 1,
          opacity: 0.4,
        },
      },
      labels: {
        rotate: -45,
      },
      tooltip: {
        enabled: false, // Disable separate x-axis tooltip
      },
    },
    yaxis: {
      title: {
        text: priceView === 'kg' ? 'FOB USD/kg' : 'FOB USD/Caja',
      },
      labels: {
        formatter: (value) => `$${value ? value.toFixed(2) : 0}`,
      },
    },
    grid: {
      show: true,
      xaxis: {
        lines: { show: true },
      },
    },
    tooltip: {
      intersect: false,
      followCursor: true,
      shared: true,
      fixed: {
        enabled: false,
      },
      custom: function ({ dataPointIndex, w }) {
        const weekNumber = w.globals.categoryLabels[dataPointIndex];
        const fullWeek = weeks.find((w) => w.split('-W')[1] === weekNumber);
        const weekData = fullWeek
          ? data[selectedVariety]?.[fullWeek]?.[selectedCaliber]
          : null;

        const multiplier = priceView === 'box' ? 5 : 1;

        // Get boxplot data values - use the boxplot series data, which is always series 0
        const boxplotSeriesIndex = 0; // Always use the boxplot series data
        const min =
          w.globals.seriesCandleO[boxplotSeriesIndex][dataPointIndex] || 0;
        const q1 =
          w.globals.seriesCandleH[boxplotSeriesIndex][dataPointIndex] || 0;
        const median =
          w.globals.seriesCandleM[boxplotSeriesIndex][dataPointIndex] || 0;
        const q3 =
          w.globals.seriesCandleL[boxplotSeriesIndex][dataPointIndex] || 0;
        const max =
          w.globals.seriesCandleC[boxplotSeriesIndex][dataPointIndex] || 0;

        // Generate own data section - safeguard against null/undefined own data
        const ownValuesSection =
          weekData?.own &&
            Array.isArray(weekData.own) &&
            weekData.own.length > 0
            ? weekData.own
              .map((ownData) => {
                if (!ownData) return '';

                const identifier =
                  separation === 'by_label'
                    ? ownData.label || 'Sin etiqueta'
                    : separation === 'by_receiver'
                      ? ownData.receiver || 'Sin receptor'
                      : 'PROPIO';

                const percentile = calculatePercentile(
                  ownData.value * multiplier,
                  {
                    min,
                    q1,
                    median,
                    q3,
                    max,
                  }
                );

                return `
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #4CAF50; font-weight: bold;">${identifier}</span>
                    <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${(
                    ownData.value * multiplier
                  ).toFixed(2)}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #4CAF50; font-weight: bold;">Percentil</span>
                    <span style="font-weight: bold; color: #000000; margin-left: 10px;">${percentile}%</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #4CAF50; font-weight: bold;">Kilos</span>
                    <span style="font-weight: bold; color: #000000; margin-left: 10px;">${ownData.kilos
                    ? ownData.kilos.toLocaleString('es-CL')
                    : '0'
                  } kg</span>
                  </div>
                  ${weekData.own && weekData.own.length > 1
                    ? '<div style="border-top: 1px solid #eee; margin: 8px 0;"></div>'
                    : ''
                  }
                `;
              })
              .join('')
            : `
            <div style="font-size: 12px; color: #666; text-align: center;">
              No hay datos propios para esta semana
            </div>
          `;

        // Create the boxplot section only if we have valid data
        const boxPlotSection =
          min && max && q1 && q3 && median
            ? `
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: #666;">Máximo:</span>
            <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${max.toFixed(
              2
            )}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: #666;">Q3:</span>
            <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${q3.toFixed(
              2
            )}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: #666;">Mediana:</span>
            <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${median.toFixed(
              2
            )}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: #666;">Q1:</span>
            <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${q1.toFixed(
              2
            )}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: #666;">Mínimo:</span>
            <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${min.toFixed(
              2
            )}</span>
          </div>
        `
            : '';

        // Always show both the boxplot data and own data, regardless of which element is hovered
        return `
          <div style="background-color: #f8f9fa; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); padding: 12px; font-family: Arial, sans-serif;">
            <div style="text-align: center; font-size: 16px; font-weight: bold; color: #333; margin-bottom: 2px;">
              Semana ${weekNumber}
            </div>
            ${boxPlotSection}
            ${boxPlotSection &&
            weekData?.own &&
            Array.isArray(weekData.own) &&
            weekData.own.length > 0
            ? '<div style="border-top: 1px solid #eee; margin: 8px 0;"></div>'
            : ''
          }
            ${ownValuesSection}
            ${weekData?.othersKg
            ? `
              <div style="border-top: 1px solid #eee; margin: 8px 0;"></div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #666;">Kilos Otras:</span>
                <span style="font-weight: bold; color: #000000; margin-left: 10px;">${weekData.othersKg.toLocaleString(
              'es-CL'
            )} kg</span>
              </div>
            `
            : ''
          }
          </div>
        `;
      },
    },
  };

  const prepareChartData = () => {
    if (!selectedVariety || !selectedCaliber) {
      return [];
    }

    const multiplier = priceView === 'box' ? 5 : 1;

    const boxPlotSeries = {
      name: 'OTRAS EXPORTADORAS',
      type: 'boxPlot',
      data: weeks.map((week) => ({
        x: week.split('-W')[1], // Show only week number
        y:
          data[selectedVariety]?.[week]?.[selectedCaliber]?.othersKg !==
            undefined
            ? [
              data[selectedVariety][week][selectedCaliber].others.min *
              multiplier,
              data[selectedVariety][week][selectedCaliber].others.q1 *
              multiplier,
              data[selectedVariety][week][selectedCaliber].others.median *
              multiplier,
              data[selectedVariety][week][selectedCaliber].others.q3 *
              multiplier,
              data[selectedVariety][week][selectedCaliber].others.max *
              multiplier,
            ]
            : [null, null, null, null, null],
      })),
    };

    // Fix scatterSeries type
    const scatterSeries: {
      name: string;
      type: string;
      data: { x: string; y: number | null }[];
      color?: string;
    }[] = [];
    const uniqueIdentifiers = new Set<string>();

    // First pass: collect all unique identifiers (labels or receivers)
    weeks.forEach((week) => {
      const weekData = data[selectedVariety]?.[week]?.[selectedCaliber];
      if (weekData?.own) {
        weekData.own.forEach((ownData) => {
          const identifier =
            separation === 'by_label'
              ? ownData.label || 'Sin etiqueta'
              : separation === 'by_receiver'
                ? ownData.receiver || 'Sin receptor'
                : 'PROPIO';
          uniqueIdentifiers.add(identifier);
        });
      }
    });

    // Create a scatter series for each unique identifier
    Array.from(uniqueIdentifiers).forEach((identifier) => {
      const series = {
        name: identifier as string,
        type: 'scatter',
        data: weeks.map((week) => {
          const weekData = data[selectedVariety]?.[week]?.[selectedCaliber];
          const matchingOwnData = weekData?.own?.find((ownData) => {
            const ownIdentifier =
              separation === 'by_label'
                ? ownData.label
                : separation === 'by_receiver'
                  ? ownData.receiver
                  : 'PROPIO';
            return ownIdentifier === identifier;
          });

          return {
            x: week.split('-W')[1],
            y: matchingOwnData ? matchingOwnData.value * multiplier : null,
          };
        }),
      };
      scatterSeries.push(series);
    });

    return [boxPlotSeries, ...scatterSeries];
  };

  // Helper function to calculate weighted average FOB
  const calculateWeightedOwnFOB = (
    ownData:
      | {
        label?: string;
        receiver?: string;
        value: number;
        kilos: number;
      }[]
      | null
  ) => {
    if (!ownData?.length) return null;
    const totalKilos = ownData.reduce(
      (sum, item) => sum + (item.kilos || 0),
      0
    );
    if (totalKilos === 0) return null;
    const weightedSum = ownData.reduce(
      (sum, item) => sum + item.value * (item.kilos || 0),
      0
    );
    return weightedSum / totalKilos;
  };

  const exportToExcel = () => {
    const multiplier = priceView === 'box' ? 5 : 1;
    const fileName = `FOB_Evolución_${selectedVariety}_${selectedCaliber}.xlsx`;
    let excelData: any[] = [];

    // Create header row
    excelData.push([
      'Estadística',
      ...weeks.map((week) => `Semana ${week.split('-W')[1]}`),
    ]);

    // Create data rows for each statistic
    if (separation === 'none') {
      // Standard single-row statistics
      const statistics = [
        { key: 'own', label: 'FOB PROPIO' },
        { key: 'percentile', label: 'Percentil' },
        { key: 'max', label: 'Máximo' },
        { key: 'q3', label: 'Q3' },
        { key: 'median', label: 'Mediana' },
        { key: 'q1', label: 'Q1' },
        { key: 'min', label: 'Mínimo' },
        { key: 'ownKg', label: 'Kilos Propios' },
        { key: 'othersKg', label: 'Kilos Otras' },
      ];

      statistics.forEach(({ key, label }) => {
        const rowData = [label];
        weeks.forEach((week) => {
          const weekData = data[selectedVariety]?.[week]?.[selectedCaliber];

          if (weekData) {
            if (key === 'own') {
              const avgFOB = calculateWeightedOwnFOB(weekData.own);
              rowData.push(avgFOB ? (avgFOB * multiplier).toFixed(2) : '-');
            } else if (key === 'percentile') {
              const avgFOB = calculateWeightedOwnFOB(weekData.own);
              if (avgFOB) {
                const percentile = calculatePercentile(avgFOB, weekData.others);
                rowData.push(percentile !== null ? `${percentile}%` : '-');
              } else {
                rowData.push('-');
              }
            } else if (key === 'ownKg') {
              const totalKilos = weekData.own.reduce(
                (sum, item) => sum + (item.kilos || 0),
                0
              );
              rowData.push(
                totalKilos ? totalKilos.toLocaleString('es-CL') : '-'
              );
            } else if (key === 'othersKg') {
              rowData.push(
                weekData.othersKg
                  ? weekData.othersKg.toLocaleString('es-CL')
                  : '-'
              );
            } else {
              rowData.push(
                weekData.others[key as keyof typeof weekData.others]
                  ? (
                    weekData.others[key as keyof typeof weekData.others] *
                    multiplier
                  ).toFixed(2)
                  : '-'
              );
            }
          } else {
            rowData.push('-');
          }
        });
        excelData.push(rowData);
      });
    } else {
      // Separated statistics with multiple rows per identifier
      const identifiers = new Set<string>();

      // Collect all unique identifiers
      Object.values(data[selectedVariety] || {}).forEach((weekData) => {
        Object.values(weekData || {}).forEach((caliberData) => {
          caliberData?.own?.forEach((ownData) => {
            const identifier =
              separation === 'by_label' ? ownData.label : ownData.receiver;
            identifiers.add(identifier);
          });
        });
      });

      // Add market statistics first
      const marketStats = [
        { key: 'max', label: 'Máximo' },
        { key: 'q3', label: 'Q3' },
        { key: 'median', label: 'Mediana' },
        { key: 'q1', label: 'Q1' },
        { key: 'min', label: 'Mínimo' },
        { key: 'othersKg', label: 'Kilos Otras' },
      ];

      marketStats.forEach(({ key, label }) => {
        const rowData = [label];
        weeks.forEach((week) => {
          const weekData = data[selectedVariety]?.[week]?.[selectedCaliber];
          if (weekData) {
            if (key === 'othersKg') {
              rowData.push(
                weekData.othersKg
                  ? weekData.othersKg.toLocaleString('es-CL')
                  : '-'
              );
            } else {
              rowData.push(
                weekData.others[key as keyof typeof weekData.others]
                  ? (
                    weekData.others[key as keyof typeof weekData.others] *
                    multiplier
                  ).toFixed(2)
                  : '-'
              );
            }
          } else {
            rowData.push('-');
          }
        });
        excelData.push(rowData);
      });

      // Add separator row
      excelData.push(Array(weeks.length + 1).fill(''));

      // Add data for each identifier
      identifiers.forEach((identifier) => {
        // FOB row
        const fobRow = [`FOB ${identifier}`];
        // Percentile row
        const percentileRow = [`Percentil ${identifier}`];
        // Kilos row
        const kilosRow = [`Kilos ${identifier}`];

        weeks.forEach((week) => {
          const weekData = data[selectedVariety]?.[week]?.[selectedCaliber];
          const ownData = weekData?.own?.find(
            (item) =>
              (separation === 'by_label' ? item.label : item.receiver) ===
              identifier
          );

          if (ownData) {
            fobRow.push((ownData.value * multiplier).toFixed(2));
            const percentile = calculatePercentile(
              ownData.value,
              weekData.others
            );
            percentileRow.push(percentile !== null ? `${percentile}%` : '-');
            kilosRow.push(ownData.kilos.toLocaleString('es-CL'));
          } else {
            fobRow.push('-');
            percentileRow.push('-');
            kilosRow.push('-');
          }
        });

        excelData.push(fobRow, percentileRow, kilosRow);
        // Add separator row between identifiers
        excelData.push(Array(weeks.length + 1).fill(''));
      });
    }

    // Apply some basic styling
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Set column widths
    const colWidths = Array(excelData[0].length).fill({ wch: 12 });
    colWidths[0] = { wch: 15 }; // Make first column wider for labels
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, fileName);
  };

  // Update TableView component
  const TableView = ({
    data,
    selectedVariety,
    selectedCaliber,
    priceView,
  }: {
    data: ComparativeData;
    selectedVariety: string;
    selectedCaliber: string;
    priceView: 'kg' | 'box';
  }) => {
    const multiplier = priceView === 'box' ? 5 : 1;

    return (
      <div className='overflow-x-auto'>
        <table className='min-w-full border-collapse'>
          <thead>
            <tr className='bg-default-100'>
              <th className='border px-4 py-2'>Estadística</th>
              {weeks.map((week) => (
                <th key={week} className='border px-4 py-2'>
                  Semana {week.split('-W')[1]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              { key: 'own', label: 'FOB PROPIO' },
              { key: 'percentile', label: 'Percentil PROPIO' },
              { key: 'ownKg', label: 'Kilos Propios' },
              { key: 'max', label: 'Máximo' },
              { key: 'q3', label: 'Q3' },
              { key: 'median', label: 'Mediana' },
              { key: 'q1', label: 'Q1' },
              { key: 'min', label: 'Mínimo' },
              { key: 'othersKg', label: 'Kilos Otras' },
            ].map(({ key, label }) => (
              <tr
                key={key}
                className={
                  ['own', 'percentile', 'ownKg'].includes(key)
                    ? 'bg-green-50 dark:bg-green-950'
                    : ''
                }
              >
                <td
                  className={`border px-4 py-2 font-medium ${['own', 'percentile', 'ownKg'].includes(key)
                    ? 'text-green-700 dark:text-green-300'
                    : ''
                    }`}
                >
                  {label}
                </td>
                {weeks.map((week) => {
                  const weekData =
                    data[selectedVariety]?.[week]?.[selectedCaliber];

                  return (
                    <td
                      key={week}
                      className={`border px-4 py-2 text-center ${['own', 'percentile', 'ownKg'].includes(key)
                        ? 'font-semibold text-green-700 dark:text-green-300'
                        : ''
                        }`}
                    >
                      {(() => {
                        if (key === 'own') {
                          if (!weekData?.own || weekData.own.length === 0) {
                            return '-';
                          } else if (separation === 'none') {
                            const avgFOB = calculateWeightedOwnFOB(
                              weekData.own
                            );
                            return avgFOB
                              ? `$${(avgFOB * multiplier).toFixed(2)}`
                              : '-';
                          } else {
                            const values = weekData.own.map((item) => {
                              const label =
                                separation === 'by_label'
                                  ? item.label
                                  : item.receiver;
                              return `${label} $${(
                                item.value * multiplier
                              ).toFixed(2)}`;
                            });
                            return values.join('\n');
                          }
                        }
                        if (key === 'percentile') {
                          if (!weekData?.own || weekData.own.length === 0) {
                            return '-';
                          } else {
                            const percentiles = weekData.own.map((item) => {
                              const percentile = calculatePercentile(
                                item.value,
                                weekData.others
                              );
                              const label =
                                separation === 'by_label'
                                  ? `${item.label} `
                                  : separation === 'by_receiver'
                                    ? `${item.receiver} `
                                    : '';
                              return percentile !== null
                                ? `${label}${percentile}%`
                                : '-';
                            });
                            return separation === 'none'
                              ? percentiles[0]
                              : percentiles.join('\n');
                          }
                        }
                        if (key === 'ownKg') {
                          if (!weekData?.own || weekData.own.length === 0) {
                            return '-';
                          } else {
                            const kgValues = weekData.own.map((item) => {
                              const label =
                                separation === 'by_label'
                                  ? `${item.label} `
                                  : separation === 'by_receiver'
                                    ? `${item.receiver} `
                                    : '';
                              return `${label}${item.kilos?.toLocaleString(
                                'es-CL'
                              )} kg`;
                            });
                            return separation === 'none'
                              ? `${weekData.own
                                .reduce(
                                  (sum, item) => sum + (item.kilos || 0),
                                  0
                                )
                                .toLocaleString('es-CL')} kg`
                              : kgValues.join('\n');
                          }
                        }
                        if (key === 'othersKg') {
                          return weekData?.othersKg
                            ? `${weekData.othersKg.toLocaleString('es-CL')} kg`
                            : '-';
                        }
                        return weekData?.others[
                          key as keyof typeof weekData.others
                        ]
                          ? `$${(
                            weekData.others[
                            key as keyof typeof weekData.others
                            ] * multiplier
                          ).toFixed(2)}`
                          : '-';
                      })()}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      {/* Sticky Sidebar */}
      <ModalSlideFilter
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
      >
        <div className='bg-background p-4 pt-8 h-full overflow-y-auto'>
          <div className='space-y-4'>
            <div className='flex items-center justify-between'>
              <p className='text-sm text-default-500 mb-1'>Metodología</p>
              <Button
                isIconOnly
                size='sm'
                variant='light'
                onPress={() => setIsMethodologyModalOpen(true)}
              >
                <Icon icon='tabler:help-circle' className='text-default-400' />
              </Button>
            </div>

            <Select
              label='Ponderación'
              selectedKeys={[methodology]}
              onSelectionChange={(keys) =>
                setMethodology(
                  Array.from(keys)[0] as 'by_volume' | 'by_exporter'
                )
              }
              className='w-full mb-4'
              isDisabled={selectedSpecies?.id !== 2 || loading}
            >
              <SelectItem key='by_volume' value='by_volume'>
                Por Volumen
              </SelectItem>
              <SelectItem key='by_exporter' value='by_exporter'>
                Por Exportadora
              </SelectItem>
            </Select>
            <Select
              label='Fecha'
              selectedKeys={[dateMethodology]}
              onSelectionChange={(keys) =>
                setDateMethodology(
                  Array.from(keys)[0] as 'date_sold' | 'eta' | 'etd'
                )
              }
              className='w-full mb-4'
              isDisabled={selectedSpecies?.id !== 2 || loading}
            >
              <SelectItem key='date_sold' value='date_sold'>
                Fecha Venta Liquidación
              </SelectItem>
              <SelectItem key='eta' value='eta'>
                Fecha Arribo Nave
              </SelectItem>
              <SelectItem key='etd' value='etd'>
                Fecha Zarpe Nave
              </SelectItem>
            </Select>

            <p className='text-sm text-default-500 mb-1'>Filtros</p>
            <SeasonFilter disabled={selectedSpecies?.id !== 2 || loading} />
            <Select
              label='Filtro Siniestros'
              selectedKeys={[sinisterFilter]}
              onSelectionChange={(keys) =>
                setSinisterFilter(Array.from(keys)[0] as typeof sinisterFilter)
              }
              className='w-full mb-4'
              isDisabled={selectedSpecies?.id !== 2 || loading}
            >
              <SelectItem key='all' value='all'>
                Todos
              </SelectItem>
              <SelectItem key='sinister' value='sinister'>
                Solo Siniestros
              </SelectItem>
              <SelectItem key='non_sinister' value='non_sinister'>
                Sin Siniestros
              </SelectItem>
            </Select>
            <p className='text-sm text-default-500 mb-1'>Unidades</p>
            <Select
              label='Unidad de precio'
              value={priceView}
              onChange={(e) => setPriceView(e.target.value as 'kg' | 'box')}
              defaultSelectedKeys={['kg']}
              className='w-full'
              isDisabled={selectedSpecies?.id !== 2 || loading}
            >
              <SelectItem key='kg' value='kg' textValue='Precio por kg'>
                Precio por kg
              </SelectItem>
              <SelectItem
                key='box'
                value='box'
                textValue='Precio por caja - 5kgs'
              >
                Precio por caja - 5kgs
              </SelectItem>
            </Select>

            <p className='text-sm text-default-500 mb-1'>Visualización</p>
            <Select
              label='Tipo de visualización'
              selectedKeys={[visualization]}
              onSelectionChange={(keys) =>
                setVisualization(Array.from(keys)[0] as 'chart' | 'table')
              }
              className='w-full'
              isDisabled={selectedSpecies?.id !== 2 || loading}
            >
              <SelectItem key='chart' value='chart'>
                Gráficos
              </SelectItem>
              <SelectItem key='table' value='table'>
                Tablas
              </SelectItem>
            </Select>
            <Select
              label='Separación'
              selectedKeys={[separation]}
              onSelectionChange={(keys) =>
                setSeparation(
                  Array.from(keys)[0] as 'none' | 'by_label' | 'by_receiver'
                )
              }
              className='w-full mb-4'
              isDisabled={selectedSpecies?.id !== 2 || loading}
            >
              <SelectItem key='none' value='none'>
                Sin separación
              </SelectItem>
              <SelectItem key='by_label' value='by_label'>
                Por Etiqueta
              </SelectItem>
              <SelectItem key='by_receiver' value='by_receiver'>
                Por Recibidor
              </SelectItem>
            </Select>
          </div>
        </div>
      </ModalSlideFilter>

      <MethologyFobExportersModal
        isOpen={isMethodologyModalOpen}
        onClose={() => setIsMethodologyModalOpen(false)}
      />

      <PageLayout>
        <Header
          title='Comparativa Industria por Semana'
          description='Analiza la evolución de precios FOB entre exportadoras que usan CherryMarket.'
        />

        <div className='mb-4'>
          <FiltersToggle
            isOpen={isFilterOpen}
            onToggle={() => setIsFilterOpen(!isFilterOpen)}
            label={isFilterOpen ? 'Ocultar filtros' : 'Mostrar filtros'}
          />
        </div>

        {userProfile?.exporter?.hasDemo && (
          <Alert className='mb-4'>
            Estás viendo una versión de prueba. Contrata CherryMarket para ver
            los datos completos.
          </Alert>
        )}

        {selectedSpecies?.id !== 2 && (
          <Alert variant='danger' className='mb-4'>
            Esta visualización solo está disponible para cerezas. Por favor
            selecciona cereza en el filtro de especies.
          </Alert>
        )}

        <Card
          className={`p-4 ${selectedSpecies?.id !== 2 ? 'opacity-50' : ''}`}
        >
          <CardHeader className='flex-col gap-4'>
            <div className='flex justify-between items-center w-full'>
              <h2 className='text-lg font-semibold'>
                FOB por Semana, {selectedVariety} - Calibre {selectedCaliber}
              </h2>
              <Button
                size='sm'
                variant='flat'
                startContent={<Icon icon='tabler:download' />}
                onClick={exportToExcel}
                isDisabled={selectedSpecies?.id !== 2}
              >
                Exportar a Excel
              </Button>
            </div>
            <div className='flex gap-4 w-full'>
              <Select
                label='Variedad'
                selectedKeys={selectedVariety ? [selectedVariety] : []}
                onSelectionChange={(keys) =>
                  setSelectedVariety(Array.from(keys)[0] as string)
                }
                className='flex-1'
                isDisabled={
                  varietiesLoading ||
                  loading ||
                  userProfile?.exporter?.hasDemo ||
                  selectedSpecies?.id !== 2
                }
                description={
                  userProfile?.exporter?.hasDemo
                    ? 'Contrata CherryMarket para ver todas las variedades'
                    : ''
                }
              >
                {varieties.map((varietyData) => (
                  <SelectItem
                    key={varietyData.variety}
                    value={varietyData.variety}
                  >
                    {varietyData.variety}
                  </SelectItem>
                ))}
              </Select>
              <Select
                label='Calibre'
                selectedKeys={[selectedCaliber]}
                onSelectionChange={(keys) =>
                  setSelectedCaliber(Array.from(keys)[0] as string)
                }
                className='flex-1'
                isDisabled={
                  loading ||
                  userProfile?.exporter?.hasDemo ||
                  selectedSpecies?.id !== 2
                }
                description={
                  userProfile?.exporter?.hasDemo
                    ? 'Contrata CherryMarket para ver todos los calibres'
                    : ''
                }
              >
                {CALIBERS.map((caliber) => (
                  <SelectItem key={caliber} value={caliber}>
                    {caliber}
                  </SelectItem>
                ))}
              </Select>
            </div>
          </CardHeader>
          <CardBody>
            {selectedSpecies?.id !== 2 ? (
              <EmptyState
                title='Esta visualización solo está disponible para cerezas'
                description='Por favor selecciona cereza en el filtro de especies para ver este análisis'
              />
            ) : loading ? (
              <LoadingState />
            ) : visualization === 'chart' ? (
              <ReactApexChart
                options={chartOptions}
                series={prepareChartData()}
                type='boxPlot'
                height={500}
              />
            ) : (
              <TableView
                data={data}
                selectedVariety={selectedVariety}
                selectedCaliber={selectedCaliber}
                priceView={priceView}
              />
            )}
          </CardBody>
        </Card>
      </PageLayout>
    </div>
  );
};

export default SettlementsComparativeExportersEvolution;
